/**
 * Workspace Configuration API Service
 *
 * Handles persistence of user workspace layouts to the backend.
 * Each user has a single configuration document storing their complete workspace state.
 */

import { apiRequest } from "../client";
import type { WorkspaceConfigRequest, WorkspaceConfigResponse } from "./types";

const BASE_URL = "/workspace-config";

/**
 * Sanitize workspace config to ensure all numeric position/size values are integers.
 * The backend schema requires integer values for x, y, width, height, and zIndex.
 *
 * @param config - The workspace configuration to sanitize
 * @returns A new config with all position/size values rounded to integers
 */
function sanitizeConfig(config: WorkspaceConfigRequest): WorkspaceConfigRequest {
  return {
    ...config,
    windows: config.windows.map((window) => ({
      ...window,
      position: {
        x: Math.round(window.position.x),
        y: Math.round(window.position.y),
      },
      size: {
        width: Math.round(window.size.width),
        height: Math.round(window.size.height),
      },
      zIndex: Math.round(window.zIndex),
    })),
  };
}

/**
 * Retrieve the current user's workspace configuration.
 *
 * @returns The workspace configuration or null if none exists
 * @throws Re-throws non-404 errors for caller handling
 */
export async function getWorkspaceConfig(): Promise<WorkspaceConfigResponse | null> {
  try {
    return await apiRequest<WorkspaceConfigResponse>({
      method: "GET",
      url: BASE_URL,
    });
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Create or replace the current user's workspace configuration.
 *
 * Uses upsert semantics - creates if none exists, replaces entirely if one exists.
 * Automatically sanitizes position/size values to integers as required by the backend.
 *
 * @param config - The complete workspace configuration to save
 * @returns The saved configuration with server-generated fields
 */
export async function saveWorkspaceConfig(
  config: WorkspaceConfigRequest
): Promise<WorkspaceConfigResponse> {
  const sanitizedConfig = sanitizeConfig(config);
  return apiRequest<WorkspaceConfigResponse>({
    method: "PUT",
    url: BASE_URL,
    data: sanitizedConfig,
  });
}

export type { WorkspaceConfigRequest, WorkspaceConfigResponse } from "./types";
